/**
 * MediaPreview.tsx - Media Preview and Display Component
 *
 * This component provides real-time preview of media during editing with
 * zoom controls, pan functionality, and editing overlay indicators.
 *
 * Key Features:
 * - Real-time preview of editing changes
 * - Zoom in/out with mouse wheel and controls
 * - Pan and scroll for detailed viewing
 * - Editing overlay indicators (crop areas, adjustment zones)
 * - Fit-to-screen and actual size viewing modes
 * - Before/after comparison toggle
 *
 * Preview Modes:
 * - Fit to Screen: Scale image to fit preview area
 * - Actual Size: Display image at 100% resolution
 * - Custom Zoom: User-defined zoom levels
 * - Full Screen: Expanded preview for detailed work
 *
 * Interactive Features:
 * - Mouse wheel zoom with center point preservation
 * - Click and drag panning for navigation
 * - Keyboard shortcuts for zoom and pan
 * - Touch gestures for mobile devices
 * - Crop area overlay with drag handles
 *
 * Visual Indicators:
 * - Crop boundaries with resize handles
 * - Grid overlay for alignment assistance
 * - Zoom level indicator and controls
 * - Loading states during processing
 * - Error states for unsupported media
 *
 * Props:
 * - mediaUrl: string - URL of media to preview
 * - editingMode: string - Current editing mode for overlays
 * - cropArea?: CropArea - Current crop selection area
 * - onCropChange?: (crop: CropArea) => void - Crop area change handler
 *
 * Dependencies: Canvas API, zoom/pan utilities, gesture handling
 */

import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { EditingTool } from "./EditingPanel";
import { MediaType } from "../../enums";

const PreviewContainer = styled.div<{ isZoomMode: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  padding: 24px;
  padding: 24px;
  cursor: ${(props) => (props.isZoomMode ? "grab" : "default")};

  &:active {
    cursor: ${(props) => (props.isZoomMode ? "grabbing" : "default")};
  }
`;

const MediaElement = styled.div<{
  zoom: number;
  panX: number;
  panY: number;
  isProcessing: boolean;
  isZoomMode: boolean;
}>`
  transform: scale(${(props) => props.zoom})
    translate(${(props) => props.panX}px, ${(props) => props.panY}px);
  transition: ${(props) =>
    props.isProcessing || props.isZoomMode ? "none" : "transform 0.2s ease"};
  position: relative;
  filter: ${(props) => (props.isProcessing ? "blur(2px)" : "none")};
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);

  img,
  iframe {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
    display: block;
    border-radius: 0;
  }
`;

const ProcessingOverlay = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: ${(props) => (props.visible ? "flex" : "none")};
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(4px);
`;

const ProcessingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

interface MediaPreviewProps {
  src: string;
  type: keyof typeof MediaType;
  activeTool: EditingTool;
  zoom?: number;
  panX?: number;
  panY?: number;
  isProcessing: boolean;
  adjust?: {
    brightness: number;
    contrast: number;
    saturation: number;
    blur: number;
  };
  rotation?: number;
  flipH?: boolean;
  flipV?: boolean;
  onLoad?: () => void;
  cropData?: {
    x: number;
    y: number;
    width: number;
    height: number;
  } | null;
  showCroppedOnly?: boolean;
  cropConstrainedZoom?: boolean;
}

const MediaPreviewComponent = React.forwardRef<
  HTMLImageElement,
  MediaPreviewProps
>(
  (
    {
      src,
      type,
      activeTool,
      zoom = 1,
      panX = 0,
      panY = 0,
      isProcessing,
      adjust = { brightness: 100, contrast: 100, saturation: 100, blur: 0 },
      rotation = 0,
      flipH = false,
      flipV = false,
      onLoad,
      cropData = null,
      showCroppedOnly = false,
      cropConstrainedZoom = false,
    },
    ref
  ) => {
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [currentPan, setCurrentPan] = useState({ x: panX, y: panY });
    const containerRef = useRef<HTMLDivElement>(null);

    const isZoomMode = activeTool === "zoom";

    // Update pan when props change
    useEffect(() => {
      setCurrentPan({ x: panX, y: panY });
    }, [panX, panY]);

    const handleMouseDown = (e: React.MouseEvent) => {
      if (!isZoomMode || zoom <= 1) return;

      setIsDragging(true);
      setDragStart({
        x: e.clientX - currentPan.x,
        y: e.clientY - currentPan.y,
      });
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !isZoomMode) return;

      const newX = e.clientX - dragStart.x;
      const newY = e.clientY - dragStart.y;

      // Apply zoom boundaries - crop-constrained or full image
      const container = containerRef.current;
      if (container) {
        const containerRect = container.getBoundingClientRect();

        let maxPanX, maxPanY;

        if (cropConstrainedZoom && cropData) {
          // Calculate pan limits based on crop dimensions
          const cropWidth = containerRect.width * cropData.width;
          const cropHeight = containerRect.height * cropData.height;
          maxPanX = (cropWidth * (zoom - 1)) / 2;
          maxPanY = (cropHeight * (zoom - 1)) / 2;
        } else {
          // Original full-image pan limits
          maxPanX = (containerRect.width * (zoom - 1)) / 2;
          maxPanY = (containerRect.height * (zoom - 1)) / 2;
        }

        const boundedX = Math.max(-maxPanX, Math.min(maxPanX, newX));
        const boundedY = Math.max(-maxPanY, Math.min(maxPanY, newY));

        setCurrentPan({ x: boundedX, y: boundedY });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    // Add global mouse event listeners when dragging
    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);

        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    }, [isDragging, dragStart, zoom]);

    const getVideoEmbedUrl = (url: string) => {
      if (url.includes("youtube.com/watch?v=")) {
        const videoId = url.split("v=")[1]?.split("&")[0];
        return `https://www.youtube.com/embed/${videoId}`;
      }
      return url;
    };

    const { brightness, contrast, saturation, blur } = adjust;
    const filterString = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%) blur(${blur}px)`;

    // Build transform string with all transformations
    let transformString = `translate(${currentPan.x}px, ${currentPan.y}px) scale(${zoom}) rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`;

    // Apply crop clipping if showing cropped preview
    let clipPath = "";
    if (showCroppedOnly && cropData && type === MediaType.Image) {
      // Convert relative crop coordinates to CSS clip-path
      const leftPercent = cropData.x * 100;
      const topPercent = cropData.y * 100;
      const rightPercent = (cropData.x + cropData.width) * 100;
      const bottomPercent = (cropData.y + cropData.height) * 100;

      clipPath = `inset(${topPercent}% ${100 - rightPercent}% ${100 - bottomPercent}% ${leftPercent}%)`;
    }

    return (
      <PreviewContainer
        ref={containerRef}
        isZoomMode={isZoomMode}
        onMouseDown={handleMouseDown}
      >
        {type === MediaType.Image ? (
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              top: 0,
              left: 0,
              transform: transformString,
              transformOrigin: "center center",
              pointerEvents: "none",
            }}
          >
            <img
              ref={ref}
              src={src}
              alt="image-preview"
              draggable={false}
              onLoad={() => {
                if (onLoad) {
                  onLoad();
                }
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "contain",
                userSelect: "none",
                filter: isProcessing ? "blur(2px)" : filterString,
                clipPath: clipPath || "none",
              }}
            />
          </div>
        ) : (
          <iframe
            src={getVideoEmbedUrl(src)}
            width="100%"
            height="100%"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{
              width: "100%",
              height: "100%",
              border: "none",
              transform: transformString,
              transition:
                isProcessing || isZoomMode ? "none" : "transform 0.2s ease",
              filter: isProcessing ? "blur(2px)" : "none",
              clipPath: showCroppedOnly && cropData ? clipPath : "none",
            }}
          />
        )}

        <ProcessingOverlay visible={isProcessing}>
          <ProcessingSpinner />
        </ProcessingOverlay>
      </PreviewContainer>
    );
  }
);

MediaPreviewComponent.displayName = "MediaPreview";

export const MediaPreview = MediaPreviewComponent;
